// Rendert eine Szene aus Daten: gebackene Ebenen, Figuren, Licht, Partikel, Kamera.
import { Application, Container, Sprite, Texture, Graphics, BlurFilter, CanvasSource } from 'pixi.js';
import type { SceneDef, PropDef, Pt, HotspotDef, Cond } from '../../shared/types.ts';
import { NavMesh } from '../../shared/nav.ts';
import { painters } from '../art/index.ts';
import { Rng, makeCanvas, rgba } from '../art/paint.ts';
import { P } from '../art/palette.ts';
import { Character, OUTFITS } from './character.ts';

const texCache = new Map<string, { tex: Texture; ax: number; ay: number; w: number; h: number }>();

/** Auflösung, mit der Szenengrafik gebacken wird (an Bildschirm angepasst, spart Speicher und Ladezeit) */
export let bakeRes = 1;
let targetRes = 1;
export function setBakeRes(r: number) {
  targetRes = Math.max(0.5, Math.min(1, Math.ceil(r * 4) / 4));
  bakeRes = targetRes;
}

export function bake(painter: string, params: Record<string, unknown>, seed: string) {
  const k = bakeRes;
  const key = painter + '|' + JSON.stringify(params) + '|' + seed + '|' + k;
  const hit = texCache.get(key);
  if (hit) return hit;
  const p = painters[painter];
  if (!p) throw new Error('Unbekannter Painter: ' + painter);
  const sz = p.size(params as any);
  // Rand für Schatten/Überstand
  const pad = 70;
  const c = makeCanvas((sz.w + pad * 2) * k, (sz.h + pad * 2) * k);
  const ctx = c.getContext('2d')!;
  ctx.scale(k, k);
  ctx.translate(pad, pad);
  p.draw(ctx, params as any, new Rng(seed));
  const tex = new Texture({ source: new CanvasSource({ resource: c, resolution: k }) });
  const res = { tex, ax: (sz.ax ?? sz.w / 2) + pad, ay: (sz.ay ?? sz.h) + pad, w: sz.w, h: sz.h };
  texCache.set(key, res);
  return res;
}

let lightTex: Texture | null = null;
function getLightTex(): Texture {
  if (lightTex) return lightTex;
  const c = makeCanvas(256, 256);
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.15)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  lightTex = Texture.from(c);
  return lightTex;
}

let dotTex: Texture | null = null;
function getDotTex(): Texture {
  if (dotTex) return dotTex;
  const c = makeCanvas(16, 16);
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 16, 16);
  dotTex = Texture.from(c);
  return dotTex;
}

let vignetteTex: Texture | null = null;
function getVignette(): Texture {
  if (vignetteTex) return vignetteTex;
  const c = makeCanvas(512, 288);
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(256, 150, 60, 256, 144, 320);
  g.addColorStop(0, rgba(P.night0, 0));
  g.addColorStop(0.6, rgba(P.night0, 0.12));
  g.addColorStop(1, rgba(P.night0, 0.7));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 512, 288);
  vignetteTex = Texture.from(c);
  return vignetteTex;
}

interface PropNode {
  def: PropDef;
  sprite: Sprite;
  key: string;
  bounds?: [number, number, number, number];
}

interface Particle {
  s: Sprite;
  vx: number;
  vy: number;
  life: number;
  max: number;
}

export class SceneView {
  root = new Container(); // Weltcontainer (skaliert/verschoben)
  private bg = new Sprite();
  private mid = new Container();
  private front = new Container();
  private lights = new Container();
  private fx = new Container();
  /** Wetter: ferne Ebene (Möwen, langsamer als die Szene) und nahe Ebene (Regen, Nebel, schneller) – Parallaxe */
  private skyFx = new Container();
  private weatherFx = new Container();
  private wparts: { s: Sprite | Graphics; vx: number; vy: number; kind: string; ph: number; base: number }[] = [];
  weather: 'clear' | 'rain' | 'fog' | 'storm' = 'clear';
  private hint = new Graphics();
  private vignette = new Sprite(getVignette());
  props = new Map<string, PropNode>();
  nav!: NavMesh;
  def!: SceneDef;
  player: Character;
  scale = 1;
  camX = 0;
  camY = 0;
  private camTarget = 0;
  private path: Pt[] = [];
  private walkResolve: (() => void) | null = null;
  private lightNodes: { s: Sprite; base: number; flicker: number; phase: number; showIf?: Cond }[] = [];
  private particles: Particle[] = [];
  private particleDefs: SceneDef['particles'] = [];
  private rng = new Rng('fx');
  portrait = false;
  /** sichtbarer Bildschirmbereich der Szene (CSS-Pixel) */
  viewRect = { x: 0, y: 0, w: 0, h: 0 };
  hintsUntil = 0;
  hotspotCenters: () => { id: string; x: number; y: number }[] = () => [];
  manualPan = 0;
  progressive = true;
  private refinePending = false;
  private refineQueue: (() => void)[] = [];
  private propParamsFn: ((p: PropDef) => Record<string, unknown> | null) | null = null;

  constructor(private app: Application, player: Character) {
    this.player = player;
    this.tint.visible = false;
    this.root.addChild(this.bg, this.skyFx, this.mid, this.front, this.tint, this.lights, this.fx, this.weatherFx, this.hint);
    this.lights.blendMode = 'add';
    this.app.stage.addChild(this.root, this.vignette);
  }

  npcs = new Map<string, { c: Character; showIf?: Cond }>();
  /** Tagesablauf: Figur gerade in dieser Szene? */
  npcFilter: (id: string) => boolean = () => true;
  /** Tageszeit-Färbung über der Szene (nur draußen) */
  private tint = new Sprite(Texture.WHITE);
  private lightBoost = 1;
  time = 1;

  /** Tageszeit: 0 Morgen, 1 Mittag, 2 Abend, 3 Nacht – weicher Übergang (Abblenden, Farbe wechseln, Aufblenden) */
  private tintGoal = { c: 0xffffff, a: 0, blend: 'normal' as 'normal' | 'multiply', l: 1 };
  setTime(time: number, outdoor: boolean, instant = false) {
    this.time = time;
    const look = [
      { c: 0xf6c86d, a: 0.12, l: 1 },
      { c: 0xffffff, a: 0, l: 0.8 },
      { c: 0xbf5240, a: 0.22, l: 1.3 },
      { c: 0x0c1830, a: 0.55, l: 1.8 },
    ][Math.max(0, Math.min(3, time))];
    this.tintGoal = { c: look.c, a: outdoor ? look.a : 0, blend: time === 3 ? 'multiply' : 'normal', l: outdoor ? look.l : 1 };
    if (this.def) {
      this.tint.width = this.def.width;
      this.tint.height = this.def.height;
    }
    if (instant) {
      this.tint.tint = look.c;
      this.tint.blendMode = this.tintGoal.blend;
      this.tint.alpha = this.tintGoal.a;
      this.lightBoost = this.tintGoal.l;
    }
    this.tint.visible = true;
  }

  private stepTint(dt: number) {
    const g = this.tintGoal;
    const k = Math.min(1, dt * 1.5);
    this.lightBoost += (g.l - this.lightBoost) * k;
    if ((this.tint.tint as number) !== g.c || this.tint.blendMode !== g.blend) {
      this.tint.alpha = Math.max(0, this.tint.alpha - dt * 0.8);
      if (this.tint.alpha <= 0.01) {
        this.tint.tint = g.c;
        this.tint.blendMode = g.blend;
      }
    } else this.tint.alpha += (g.a - this.tint.alpha) * k;
  }

  /** Wetter für Außenszenen: Regen, Nebel, Sturm; bei klarem Wetter am Wasser Möwen */
  setWeather(w: 'clear' | 'rain' | 'fog' | 'storm', outdoor: boolean, harbor: boolean) {
    this.weather = w;
    for (const c of [...this.skyFx.removeChildren(), ...this.weatherFx.removeChildren()]) c.destroy();
    this.wparts = [];
    if (!outdoor || !this.def) return;
    const reduced = document.documentElement.dataset.motion === 'reduced';
    const W = this.def.width, H = this.def.height;
    const add = (kind: string, n: number) => {
      for (let i = 0; i < (reduced ? Math.ceil(n / 3) : n); i++) {
        if (kind === 'gull') {
          const g = new Graphics();
          g.moveTo(-14, 0).quadraticCurveTo(-7, -8, 0, 0).quadraticCurveTo(7, -8, 14, 0).stroke({ color: P.white, width: 3 });
          g.x = this.rng.next() * W;
          g.y = 80 + this.rng.next() * H * 0.25;
          g.scale.set(this.rng.range(0.6, 1.2));
          this.skyFx.addChild(g);
          this.wparts.push({ s: g, vx: this.rng.range(25, 60) * (this.rng.chance(0.5) ? -1 : 1), vy: 0, kind, ph: this.rng.next() * 6, base: g.y });
        } else if (kind === 'fog') {
          const f = new Sprite(getLightTex());
          f.anchor.set(0.5);
          f.tint = P.plaster2 as any;
          f.width = this.rng.range(500, 900);
          f.height = f.width * 0.35;
          f.alpha = this.rng.range(0.12, 0.22);
          f.x = this.rng.next() * W;
          f.y = H * 0.45 + this.rng.next() * H * 0.45;
          this.weatherFx.addChild(f);
          this.wparts.push({ s: f, vx: this.rng.range(8, 20), vy: 0, kind, ph: 0, base: f.alpha });
        } else {
          const r = new Sprite(getDotTex());
          r.anchor.set(0.5);
          // nachts gut sichtbar: helle, kühle Regenstriche vor dunklem Grund
          r.tint = P.plaster2 as any;
          r.scale.set(0.14, kind === 'storm' ? 2.2 : 1.7);
          r.alpha = 0.32;
          r.x = this.rng.next() * W;
          r.y = this.rng.next() * H;
          r.rotation = kind === 'storm' ? 0.25 : 0.07;
          this.weatherFx.addChild(r);
          this.wparts.push({ s: r, vx: kind === 'storm' ? -260 : -40, vy: kind === 'storm' ? 900 : 620, kind, ph: 0, base: 0.4 });
        }
      }
    };
    if (w === 'rain') add('rain', 160);
    if (w === 'storm') add('storm', 260);
    if (w === 'fog') add('fog', 14);
    if (harbor && (w === 'clear' || w === 'fog')) add('gull', 6);
  }

  private stepWeather(dt: number) {
    if (!this.wparts.length || !this.def) return;
    const W = this.def.width, H = this.def.height;
    const t = performance.now() / 1000;
    for (const p of this.wparts) {
      p.s.x += p.vx * dt;
      p.s.y += p.vy * dt;
      if (p.kind === 'gull') {
        p.s.scale.y = Math.abs(p.s.scale.x) * (0.55 + 0.45 * Math.sin(t * 7 + p.ph));
        p.s.y = p.base + Math.sin(t * 0.7 + p.ph) * 12;
        if (p.s.x < -60) p.s.x = W + 60;
        if (p.s.x > W + 60) p.s.x = -60;
      } else if (p.kind === 'fog') {
        if (p.s.x > W + 450) p.s.x = -450;
      } else if (p.s.y > H) {
        p.s.y = -20;
        p.s.x = Math.random() * (W + 300);
      }
    }
  }

  load(def: SceneDef, propParams: (p: PropDef) => Record<string, unknown> | null, cond: (c: Cond | undefined) => boolean = () => true) {
    this.def = def;
    {
      const vw = this.app.screen.width, vh = this.app.screen.height;
      const s = vw / vh < 1 ? Math.min(vw / 860, (vh * 0.62) / def.height) : Math.max(vh / def.height, vw / def.width);
      setBakeRes(s * this.app.renderer.resolution);
      // Progressives Laden: erst grob (schneller erster Frame), danach im Hintergrund scharf nachbacken
      // erstes Bild sehr grob (weniger Pixel zu malen und hochzuladen), danach schrittweise scharf
      if (this.progressive && bakeRes > 0.35) {
        bakeRes = 0.35;
        this.refinePending = true;
      }
    }
    performance.mark('nav0');
    this.nav = new NavMesh(def.walk.outer, def.walk.holes ?? []);
    performance.measure('nav', 'nav0');
    performance.mark('bg0');
    const bgr = bake(def.background.painter, { width: def.width, height: def.height, ...(def.background.params ?? {}) }, def.id + ':bg');
    this.bg.texture = bgr.tex;
    performance.measure('bg', 'bg0');
    performance.mark('props0');
    this.bg.x = -(bgr.ax);
    this.bg.y = -(bgr.ay);
    this.refineQueue = [];
    for (const c of [...this.mid.children, ...this.front.children]) if (c !== this.player) c.destroy({ children: true });
    this.mid.removeChildren();
    this.front.removeChildren();
    this.props.clear();
    for (const p of def.props) {
      const sprite = new Sprite();
      const node = { def: p, sprite, key: '' };
      this.props.set(p.id, node);
      (p.layer === 'front' ? this.front : p.layer === 'back' ? this.mid : this.mid).addChild(sprite);
    }
    this.mid.addChild(this.player);
    this.npcs.clear();
    for (const n of def.npcs ?? []) {
      const c = new Character(OUTFITS[n.outfit] ?? OUTFITS.gesa);
      c.x = n.x;
      c.y = n.y;
      c.setFacing(n.face ?? 'left');
      c.scale.set(this.depthScale(n.y));
      (c as any).zIndexY = n.y;
      c.idleSeed = n.x * 0.013;
      this.mid.addChild(c);
      this.npcs.set(n.id, { c, showIf: n.showIf });
    }
    this.refreshNpcs(cond);
    this.lightCond = cond;
    this.refreshProps(propParams);
    performance.measure('props', 'props0');
    // Licht
    this.lights.removeChildren().forEach((c) => c.destroy());
    this.lightNodes = [];
    for (const l of def.lights ?? []) {
      const s = new Sprite(getLightTex());
      s.anchor.set(0.5);
      s.x = l.x;
      s.y = l.y;
      s.width = s.height = l.r * 2;
      s.tint = l.color as any;
      const base = l.intensity ?? 0.5;
      s.alpha = base;
      this.lights.addChild(s);
      this.lightNodes.push({ s, base, flicker: l.flicker ?? 0, phase: Math.random() * 10, showIf: l.showIf });
    }
    this.refreshLights(this.lightCond);
    // Partikel
    this.fx.removeChildren().forEach((c) => c.destroy());
    this.particles = [];
    this.particleDefs = def.particles ?? [];
    for (const pd of this.particleDefs) for (let i = 0; i < (pd.count ?? 30); i++) this.spawnParticle(pd, true);
    this.layout();
  }

  /** nach dem ersten Frame: Texturen in Zielauflösung nachbacken (eine pro Frame) */
  private startRefine() {
    this.refinePending = false;
    this.progressive = false;
    const def = this.def;
    bakeRes = targetRes;
    this.refineQueue = [
      () => {
        if (this.def !== def) return;
        const bgr = bake(def.background.painter, { width: def.width, height: def.height, ...(def.background.params ?? {}) }, def.id + ':bg');
        this.bg.texture = bgr.tex;
      },
      ...[...this.props.values()].map((n) => () => {
        if (this.def !== def || !n.sprite.visible) return;
        n.key = '';
        if (this.propParamsFn) this.refreshOne(n, this.propParamsFn);
      }),
    ];
  }

  refreshProps(propParams: (p: PropDef) => Record<string, unknown> | null) {
    this.propParamsFn = propParams;
    for (const node of this.props.values()) this.refreshOne(node, propParams);
  }

  private refreshOne(node: PropNode, propParams: (p: PropDef) => Record<string, unknown> | null) {
    if (node.sprite.destroyed) return;
    {
      const params = propParams(node.def);
      if (!params) {
        node.sprite.visible = false;
        return;
      }
      node.sprite.visible = true;
      const key = JSON.stringify(params);
      if (key === node.key) return;
      node.key = key;
      const r = bake(node.def.painter, params, this.def.id + ':' + node.def.id);
      node.sprite.texture = r.tex;
      node.sprite.x = node.def.x - r.ax;
      node.sprite.y = node.def.y - r.ay;
      (node.sprite as any).zIndexY = node.def.z ?? node.def.y;
      node.bounds = [node.def.x - (r.ax - 70), node.def.y - (r.ay - 70), r.w, r.h];
    }
  }

  propBounds(id: string): [number, number, number, number] | null {
    const n = this.props.get(id);
    if (!n || !n.sprite.visible || !n.bounds) return null;
    return n.bounds;
  }

  hotspotRect(h: HotspotDef): [number, number, number, number] | null {
    const a = h.area;
    if ('rect' in a) return a.rect;
    if ('npc' in a) {
      const n = this.npcs.get(a.npc);
      if (!n || !n.c.visible) return null;
      const sc = n.c.scale.y;
      return [n.c.x - 70 * sc, n.c.y - 440 * sc, 140 * sc, 440 * sc];
    }
    if ('prop' in a) {
      const b = this.propBounds(a.prop);
      if (!b) return null;
      const pad = a.pad ?? 0;
      return [b[0] - pad, b[1] - pad, b[2] + pad * 2, b[3] + pad * 2];
    }
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < a.poly.length; i += 2) {
      x0 = Math.min(x0, a.poly[i]);
      x1 = Math.max(x1, a.poly[i]);
      y0 = Math.min(y0, a.poly[i + 1]);
      y1 = Math.max(y1, a.poly[i + 1]);
    }
    return [x0, y0, x1 - x0, y1 - y0];
  }

  hitTest(h: HotspotDef, w: Pt): boolean {
    const a = h.area;
    if ('poly' in a) {
      let inside = false;
      const p = a.poly;
      for (let i = 0, j = p.length - 2; i < p.length; j = i, i += 2) {
        if (p[i + 1] > w.y !== p[j + 1] > w.y && w.x < ((p[j] - p[i]) * (w.y - p[i + 1])) / (p[j + 1] - p[i + 1]) + p[i]) inside = !inside;
      }
      return inside;
    }
    const r = this.hotspotRect(h);
    return !!r && w.x >= r[0] && w.x <= r[0] + r[2] && w.y >= r[1] && w.y <= r[1] + r[3];
  }

  placePlayer(p: Pt, face?: 'left' | 'right') {
    const q = this.nav.clamp(p);
    this.player.x = q.x;
    this.player.y = q.y;
    if (face) this.player.setFacing(face);
    this.path = [];
    this.player.walking = false;
    this.applyDepth();
    this.camTarget = this.idealCam();
    this.camX = this.camTarget;
  }

  walkTo(target: Pt): Promise<void> {
    const path = this.nav.path({ x: this.player.x, y: this.player.y }, target);
    this.path = path.slice(1);
    if (this.walkResolve) this.walkResolve();
    if (this.path.length === 0 || (this.path.length === 1 && Math.hypot(this.path[0].x - this.player.x, this.path[0].y - this.player.y) < 4)) {
      this.path = [];
      this.player.walking = false;
      return Promise.resolve();
    }
    this.player.walking = true;
    return new Promise((res) => (this.walkResolve = res));
  }

  get walking() {
    return this.path.length > 0;
  }

  private lightCond: (c: Cond | undefined) => boolean = () => true;
  refreshLights(cond: (c: Cond | undefined) => boolean) {
    this.lightCond = cond;
    for (const l of this.lightNodes) l.s.visible = cond(l.showIf);
  }

  refreshNpcs(cond: (c: Cond | undefined) => boolean) {
    for (const [id, n] of this.npcs) n.c.visible = cond(n.showIf) && this.npcFilter(id);
  }

  depthScale(y: number) {
    const d = this.def.depth;
    const t = Math.max(0, Math.min(1, (y - d.yFar) / (d.yNear - d.yFar)));
    return d.scaleFar + (d.scaleNear - d.scaleFar) * t;
  }

  private applyDepth() {
    const s = this.depthScale(this.player.y);
    this.player.scale.set(s);
    (this.player as any).zIndexY = this.player.y;
  }

  private idealCam(): number {
    const visW = this.viewRect.w / this.scale;
    const max = Math.max(0, this.def.width - visW);
    return Math.max(0, Math.min(max, this.player.x - visW / 2 + this.manualPan));
  }

  layout() {
    const vw = this.app.screen.width, vh = this.app.screen.height;
    const W = this.def?.width ?? 1920, H = this.def?.height ?? 1080;
    this.portrait = vw / vh < 1;
    if (this.portrait) {
      this.scale = Math.min(vw / 860, (vh * 0.62) / H);
      const bandH = H * this.scale;
      this.viewRect = { x: 0, y: Math.round((vh - bandH) * 0.42), w: vw, h: bandH };
    } else {
      this.scale = Math.max(vh / H, vw / W);
      this.viewRect = { x: 0, y: 0, w: vw, h: vh };
    }
    this.root.scale.set(this.scale);
    this.vignette.x = this.viewRect.x;
    this.vignette.y = this.viewRect.y;
    this.vignette.width = this.viewRect.w;
    this.vignette.height = this.viewRect.h;
    if (this.def) {
      this.camTarget = this.idealCam();
      this.camX = this.camTarget;
      this.applyCam();
    }
  }

  private applyCam() {
    const visH = this.viewRect.h / this.scale;
    this.camY = Math.max(0, this.def.height - visH); // unten bündig
    this.root.x = this.viewRect.x - this.camX * this.scale;
    this.root.y = this.viewRect.y - this.camY * this.scale;
    // Parallaxe: Himmel/Möwen bleiben zurück, Regen und Nebel ziehen schneller vorbei
    this.skyFx.x = this.camX * 0.45;
    this.weatherFx.x = -this.camX * 0.18;
  }

  screenToWorld(x: number, y: number): Pt {
    return { x: (x - this.root.x) / this.scale, y: (y - this.root.y) / this.scale };
  }
  worldToScreen(p: Pt): Pt {
    return { x: p.x * this.scale + this.root.x, y: p.y * this.scale + this.root.y };
  }
  inView(x: number, y: number) {
    const r = this.viewRect;
    return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
  }

  panBy(dxScreen: number) {
    this.manualPan -= dxScreen / this.scale;
    const visW = this.viewRect.w / this.scale;
    this.manualPan = Math.max(-this.def.width, Math.min(this.def.width, this.manualPan));
    this.camTarget = this.idealCam();
    this.camX = this.camTarget;
    // Pan über Grenzen hinaus nicht akkumulieren
    const max = Math.max(0, this.def.width - visW);
    if (this.camX <= 0 || this.camX >= max) this.manualPan = this.camX - (this.player.x - visW / 2);
  }

  private spawnParticle(pd: NonNullable<SceneDef['particles']>[number], initial = false) {
    const [x, y, w, h] = pd.rect;
    const s = new Sprite(getDotTex());
    s.anchor.set(0.5);
    let vx = 0, vy = 0, max = 10;
    if (pd.kind === 'dust') {
      s.tint = P.amber3 as any;
      s.blendMode = 'add';
      s.scale.set(this.rng.range(0.2, 0.5));
      vx = this.rng.range(-4, 4);
      vy = this.rng.range(-3, 3);
      max = this.rng.range(6, 14);
    } else if (pd.kind === 'smoke') {
      s.tint = P.plaster2 as any;
      s.scale.set(this.rng.range(2, 4));
      vx = this.rng.range(-3, 3);
      vy = this.rng.range(-14, -8);
      max = this.rng.range(4, 8);
    } else if (pd.kind === 'embers') {
      s.tint = P.amber2 as any;
      s.blendMode = 'add';
      s.scale.set(this.rng.range(0.15, 0.3));
      vx = this.rng.range(-6, 6);
      vy = this.rng.range(-30, -12);
      max = this.rng.range(1.5, 3);
    } else {
      s.tint = P.night3 as any;
      s.scale.set(0.12, 1.4);
      vx = -40;
      vy = 520;
      max = 3;
    }
    s.x = x + this.rng.next() * w;
    s.y = y + this.rng.next() * h;
    const p: Particle = { s, vx, vy, life: initial ? this.rng.next() * max : 0, max };
    (p as any).def = pd;
    this.fx.addChild(s);
    this.particles.push(p);
  }

  update(dt: number) {
    if (this.refinePending) this.startRefine();
    else if (this.refineQueue.length) this.refineQueue.shift()!();
    // Laufen
    if (this.path.length) {
      const speed = 260 * this.player.scale.y * ((window as any).__dlrFast ? 12 : 1);
      let remain = speed * dt;
      while (remain > 0 && this.path.length) {
        const tgt = this.path[0];
        const dx = tgt.x - this.player.x, dy = tgt.y - this.player.y;
        const d = Math.hypot(dx, dy);
        if (Math.abs(dx) > 2) this.player.setFacing(dx < 0 ? 'left' : 'right');
        if (d <= remain) {
          this.player.x = tgt.x;
          this.player.y = tgt.y;
          this.path.shift();
          remain -= d;
        } else {
          this.player.x += (dx / d) * remain;
          this.player.y += (dy / d) * remain;
          remain = 0;
        }
      }
      this.applyDepth();
      if (!this.path.length) {
        this.player.walking = false;
        const r = this.walkResolve;
        this.walkResolve = null;
        r?.();
      }
      this.manualPan *= 0.9;
    }
    this.player.update(dt);
    for (const n of this.npcs.values()) if (n.c.visible) n.c.update(dt);
    // Tiefensortierung
    this.mid.children.sort((a: any, b: any) => (a.zIndexY ?? 0) - (b.zIndexY ?? 0));
    // Kamera
    this.camTarget = this.idealCam();
    this.camX += (this.camTarget - this.camX) * Math.min(1, dt * 4);
    this.applyCam();
    this.stepTint(dt);
    this.stepWeather(dt);
    // Licht flackern
    const now = performance.now() / 1000;
    for (const l of this.lightNodes) {
      if (!l.flicker) {
        l.s.alpha = Math.min(1, l.base * this.lightBoost);
        continue;
      }
      const f = Math.sin(now * 7 + l.phase) * 0.5 + Math.sin(now * 13.3 + l.phase * 2) * 0.3 + Math.sin(now * 2.1) * 0.2;
      l.s.alpha = Math.min(1, l.base * this.lightBoost * (1 + f * l.flicker));
    }
    // Partikel
    for (const p of this.particles) {
      p.life += dt;
      const pd = (p as any).def;
      p.s.x += p.vx * dt;
      p.s.y += p.vy * dt;
      const k = p.life / p.max;
      if (pd.kind === 'dust') {
        p.s.alpha = Math.sin(Math.PI * Math.min(1, k)) * 0.7;
        p.vx += (Math.random() - 0.5) * 4 * dt;
      } else if (pd.kind === 'smoke') p.s.alpha = Math.sin(Math.PI * Math.min(1, k)) * 0.08;
      else if (pd.kind === 'embers') p.s.alpha = 1 - k;
      else p.s.alpha = 0.35;
      if (p.life >= p.max) {
        const [x, y, w, h] = pd.rect;
        p.life = 0;
        p.s.x = x + Math.random() * w;
        p.s.y = pd.kind === 'rain' ? y : pd.kind === 'smoke' || pd.kind === 'embers' ? y + h : y + Math.random() * h;
      }
    }
    // Hotspot-Hinweise
    this.hint.clear();
    const rem = this.hintsUntil - performance.now();
    if (rem > 0) {
      const a = Math.min(1, rem / 400);
      const pulse = 1 + Math.sin(now * 6) * 0.15;
      for (const c of this.hotspotCenters()) {
        this.hint.circle(c.x, c.y, 26 * pulse).fill({ color: P.amber3, alpha: 0.25 * a });
        this.hint.circle(c.x, c.y, 26 * pulse).stroke({ color: P.amber3, alpha: 0.9 * a, width: 4 });
        this.hint.circle(c.x, c.y, 7).fill({ color: P.white, alpha: a });
      }
    }
  }
}

export { BlurFilter };
